/**
 * Anonymous upload flow for unauthenticated users.
 * Allows one free transcription per day with device fingerprint tracking.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileVideo,
  FileAudio,
  AlertCircle,
  CheckCircle2,
  Link2,
  Loader2, UploadCloud } from 'lucide-react';
import { Button, Card } from './UIComponents';
import { SearchableSelect } from './SearchableSelect';
import { LinkImportPlatformChips } from './LinkImportPlatformChips';
import { UploadStep } from '../types';
import { useNavigate } from 'react-router-dom';
import {
  initiateAnonymousUpload,
  confirmAnonymousUpload,
  createAnonymousTranscription,
  getAnonymousQuota,
  handleApiError,
  AnonymousQuotaStatus,
} from '../api/anonymous';
import { getFingerprint } from '../utils/fingerprint';
import MediaDropzone from './MediaDropzone';
import { prepareAudioUpload } from '../utils/audioExtraction';
import { TrialLimitBanner, RegistrationPromptModal } from './RegistrationPrompt';
import axios from 'axios';
import { previewPublicLinkImport, resolveLinkImportThumbnailUrl, type LinkImportPreview } from '../api/linkImports';
import { clearLinkImportPreview, loadLinkImportPreview, saveLinkImportPreview } from '../utils/linkImportCache';
import {
  getLinkImportProviderLabel,
  isSupportedLinkImportUrl,
} from '../utils/linkImportPlatforms';

interface AnonymousUploadFlowProps {
  embedded?: boolean;
  onAuthRequired?: () => void;
}

function formatLinkDuration(seconds: number) {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

const AnonymousUploadFlow: React.FC<AnonymousUploadFlowProps> = ({
  embedded = false,
}) => {
  const [step, setStep] = useState<UploadStep>(UploadStep.SELECT_FILE);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [quota, setQuota] = useState<AnonymousQuotaStatus | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [lastTrialTranscriptionId, setLastTrialTranscriptionId] = useState<string | null>(null);
  const [entryMode, setEntryMode] = useState<'file' | 'link'>('file');
  const cachedLinkPreview = useMemo(() => loadLinkImportPreview(), []);
  const [linkUrl, setLinkUrl] = useState(cachedLinkPreview?.url ?? '');
  const [linkPreview, setLinkPreview] = useState<LinkImportPreview | null>(cachedLinkPreview?.preview ?? null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isPreviewingLink, setIsPreviewingLink] = useState(false);
  const [options, setOptions] = useState({
    language: 'auto',
    speakerDetection: false,
  });

  const navigate = useNavigate();
  const canPreviewLink = useMemo(() => isSupportedLinkImportUrl(linkUrl), [linkUrl]);

  const uploadStages = [
    { label: 'Converting to audio', duration: 0 },
    { label: 'Initializing fingerprint', duration: 0 },
    { label: 'Uploading to cloud storage', duration: 0 },
    { label: 'Confirming upload', duration: 500 },
    { label: 'Creating transcription job', duration: 500 },
    { label: 'Queuing for transcription', duration: 300 },
  ];

  useEffect(() => {
    if (!linkPreview || linkPreview.metadata_status !== 'pending') {
      return;
    }
    const previewUrl = linkPreview.normalized_url || linkUrl;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try {
        const preview = await previewPublicLinkImport(previewUrl);
        setLinkPreview(preview);
        saveLinkImportPreview(previewUrl, preview);
        if (preview.metadata_status !== 'pending' || attempts >= 15) {
          window.clearInterval(timer);
        }
      } catch {
        if (attempts >= 3) {
          window.clearInterval(timer);
        }
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [linkPreview, linkUrl]);

  const previewLink = useCallback(async () => {
    setLinkError(null);
    setLinkPreview(null);
    if (!isSupportedLinkImportUrl(linkUrl)) {
      setLinkError('Paste a valid supported media link.');
      return;
    }

    setIsPreviewingLink(true);
    try {
      const preview = await previewPublicLinkImport(linkUrl);
      setLinkPreview(preview);
      saveLinkImportPreview(linkUrl, preview);
    } catch {
      setLinkError('This media link cannot be previewed right now.');
    } finally {
      setIsPreviewingLink(false);
    }
  }, [linkUrl]);

  const clearLinkPreview = useCallback(() => {
    setLinkUrl('');
    setLinkPreview(null);
    setLinkError(null);
    clearLinkImportPreview();
  }, []);

  // Check quota on mount
  useEffect(() => {
    try {
      setLastTrialTranscriptionId(localStorage.getItem('omniscribe:last_trial_transcription_id'));
    } catch {
      // ignore
    }

    const checkQuota = async () => {
      try {
        const status = await getAnonymousQuota();
        setQuota(status);
      } catch (err) {
        // Quota banner is optional; prompt will be shown on user action.
      }
    };
    checkQuota();
  }, []);

  const uploadToPresignedUrl = useCallback(
    async (
      url: string,
      fileToUpload: File,
      onProgress: (progress: number) => void
    ) => {
      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', fileToUpload.type);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = event.loaded / event.total;
            onProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(fileToUpload);
      });
    },
    []
  );

  const startUpload = async () => {
    if (!file) return;

    // Check quota before starting
    if (quota && !quota.can_upload) {
      setShowPrompt(true);
      return;
    }

    setStep(UploadStep.UPLOADING);
    setActiveStageIndex(0);
    setProgress(0);
    setUploadError(null);

    try {
      // Stage 0: Prepare audio upload
      setActiveStageIndex(0);
      const prepared = await prepareAudioUpload(file, (progressInfo) => {
        const stageWeight = 10;
        const normalized = Math.max(0, Math.min(1, progressInfo.progress));
        if (progressInfo.stage === 'loading') {
          setProgress(Math.min(stageWeight * 0.5, normalized * stageWeight * 0.5));
          return;
        }
        setProgress(stageWeight * 0.5 + normalized * stageWeight * 0.5);
      });
      setProgress(10);

      // Stage 1: Initialize fingerprint
      setActiveStageIndex(1);
      await getFingerprint(); // Ensure fingerprint is cached
      setProgress(20);

      // Stage 2: Get presigned URL and upload
      setActiveStageIndex(2);
      const initResponse = await initiateAnonymousUpload({
        file_name: prepared.file.name,
        file_size: prepared.file.size,
        content_type: prepared.file.type,
        estimated_duration_seconds: prepared.estimatedDurationSeconds,
        language: options.language === 'auto' ? undefined : options.language,
      });

      // Upload to presigned URL
      await uploadToPresignedUrl(initResponse.upload_url, prepared.file, (uploadProgress) => {
        setProgress(20 + uploadProgress * 40);
      });
      setProgress(60);

      // Stage 3: Confirming upload
      setActiveStageIndex(3);
      await confirmAnonymousUpload(initResponse.file_id);
      await new Promise((r) => setTimeout(r, 500));
      setProgress(70);

      // Stage 4: Create transcription job
      setActiveStageIndex(4);
      const transcription = await createAnonymousTranscription({
        file_id: initResponse.file_id,
        language: options.language === 'auto' ? undefined : options.language,
        enable_diarization: options.speakerDetection,
      });
      setProgress(90);

      // Stage 5: Queuing
      setActiveStageIndex(5);
      await new Promise((r) => setTimeout(r, 300));
      setProgress(100);

      // Persist last trial transcription for convenience (viewable within 24h).
      if (transcription?.transcription_id) {
        localStorage.setItem('omniscribe:last_trial_transcription_id', transcription.transcription_id);
        navigate(`/trial/transcription/${transcription.transcription_id}`);
        return;
      }

      setStep(UploadStep.COMPLETE);
    } catch (error) {
      console.error('Upload failed:', error);

      // Check if it's a quota error
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        setShowPrompt(true);
        setUploadError('You have reached your daily trial limit. Please register to continue.');
      } else {
        setUploadError(handleApiError(error));
      }
      setStep(UploadStep.CONFIRM_INFO);
    }
  };

  const renderContent = () => {
    switch (step) {
      case UploadStep.SELECT_FILE:
        return (
          <div className="space-y-6">
            <div className="flex justify-center">
              <div role="tablist" className="seg-group">
                <button type="button" role="tab" aria-selected={entryMode === 'file'} onClick={() => setEntryMode('file')} className="seg inline-flex items-center gap-1.5">
                  <UploadCloud className="h-3.5 w-3.5" />
                  Upload File
                </button>
                <button type="button" role="tab" aria-selected={entryMode === 'link'} onClick={() => setEntryMode('link')} className="seg inline-flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" />
                  Paste Link
                </button>
              </div>
            </div>

            {/* Trial limit banner */}
            {entryMode === 'file' && quota && (
              <TrialLimitBanner
                remainingFiles={quota.remaining_files}
                dailyLimit={quota.daily_limit}
                resetTime={quota.reset_at ? new Date(quota.reset_at) : undefined}
              />
            )}

            {entryMode === 'file' && quota && !quota.can_upload && lastTrialTranscriptionId && (
              <Card className="p-4 border-slate-200 bg-white dark:bg-zinc-900 dark:border-zinc-800/60">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-left">
                    <p className="font-medium text-slate-900 dark:text-white">View your last trial transcript</p>
                    <p className="text-sm text-slate-500 dark:text-zinc-400">
                      Accessible for 24 hours on this device.
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/trial/transcription/${lastTrialTranscriptionId}`)}
                  >
                    View
                  </Button>
                </div>
              </Card>
            )}

            {entryMode === 'file' ? (
              <MediaDropzone
                embedded={embedded}
                onFileSelected={(selectedFile) => {
                  if (quota && !quota.can_upload) {
                    setShowPrompt(true);
                    return;
                  }
                  setFile(selectedFile);
                  setStep(UploadStep.CONFIRM_INFO);
                }}
              />
            ) : (
              <div className="text-center space-y-5">
                <div>
                  <p className="text-sm sm:text-base leading-relaxed text-slate-600 dark:text-zinc-400">
                    Paste a media link to preview content before transcription.
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 dark:text-zinc-400">
                    <Link2 className="h-3.5 w-3.5" />
                    Supported platforms
                  </div>
                  <LinkImportPlatformChips />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      value={linkUrl}
                      onChange={(event) => {
                        setLinkUrl(event.target.value);
                        setLinkPreview(null);
                        setLinkError(null);
                        clearLinkImportPreview();
                      }}
                      placeholder="Paste a YouTube, TikTok, Instagram, Facebook, or X link"
                      className="w-full h-12 rounded-full border border-transparent bg-card pl-11 pr-16 text-sm text-slate-900 dark:text-zinc-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {linkUrl && (
                      <button
                        type="button"
                        onClick={clearLinkPreview}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <Button
                    className="h-12 px-6 text-sm"
                    onClick={previewLink}
                    disabled={!canPreviewLink || isPreviewingLink}
                  >
                    {isPreviewingLink && <Loader2 className="h-4 w-4 animate-spin" />}
                    Search
                  </Button>
                </div>

                {linkError && (
                  <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300 text-left">
                    {linkError}
                  </div>
                )}

                {linkPreview && (
                  <div className="space-y-4 pt-1">
                    <div className="mx-auto max-w-2xl text-center">
                      <p className="text-base sm:text-lg font-semibold leading-snug text-slate-950 dark:text-white line-clamp-3">
                        {linkPreview.title || `${getLinkImportProviderLabel(linkPreview.provider)} media`}
                      </p>
                      <div className="mt-2 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 dark:text-zinc-400">
                        {linkPreview.metadata_status === 'pending' && <Loader2 className="h-4 w-4 animate-spin" />}
                        <span>
                          {linkPreview.duration > 0
                            ? formatLinkDuration(linkPreview.duration)
                            : linkPreview.metadata_status === 'pending'
                              ? 'Resolving media details...'
                              : 'Duration will be checked after import starts'}
                        </span>
                      </div>
                    </div>

                    {resolveLinkImportThumbnailUrl(linkPreview) && (
                      <img
                        src={resolveLinkImportThumbnailUrl(linkPreview)}
                        alt=""
                        onError={(event) => {
                          if (linkPreview.thumbnail_url && event.currentTarget.src !== linkPreview.thumbnail_url) {
                            event.currentTarget.src = linkPreview.thumbnail_url;
                          }
                        }}
                        className="mx-auto w-full max-w-lg rounded-lg bg-slate-100 dark:bg-zinc-900 object-cover shadow-sm"
                      />
                    )}

                    <Button onClick={() => navigate('/login')} className="mx-auto">
                      Sign in to import
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case UploadStep.CONFIRM_INFO:
        return (
          <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
            {/* Error Display */}
            {uploadError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                <p className="text-red-600 dark:text-red-400 text-sm">{uploadError}</p>
              </div>
            )}

            {/* Trial info */}
            {quota && (
              <TrialLimitBanner
                remainingFiles={quota.remaining_files}
                dailyLimit={quota.daily_limit}
                resetTime={quota.reset_at ? new Date(quota.reset_at) : undefined}
              />
            )}

            {/* File Info Card */}
            <Card className="p-1.5 pe-4 flex items-center gap-4 !bg-card !ring-0 !shadow-none">
              <div className="h-14 w-14 bg-raised rounded-[14px] flex items-center justify-center shrink-0">
                {file?.type.startsWith('video') ? (
                  <FileVideo className="text-indigo-600 dark:text-indigo-400 h-7 w-7" />
                ) : (
                  <FileAudio className="text-indigo-600 dark:text-indigo-400 h-7 w-7" />
                )}
              </div>

              <div className="flex-1 overflow-hidden text-left">
                <p
                  className="font-semibold text-slate-900 dark:text-zinc-100 truncate text-sm mb-0.5"
                  title={file?.name}
                >
                  {file?.name}
                </p>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-400">
                  <span className="uppercase bg-accent/10 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-indigo-600 dark:text-indigo-400 tracking-wide">
                    {file!.name.split('.').pop()}
                  </span>
                  <span>{(file!.size / (1024 * 1024)).toFixed(2)} MB</span>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFile(null);
                  setStep(UploadStep.SELECT_FILE);
                }}
                className="text-slate-400 hover:text-indigo-600 hover:bg-white dark:text-zinc-500 dark:hover:text-indigo-400 dark:hover:bg-zinc-800 shrink-0"
              >
                Change
              </Button>
            </Card>

            <div className="bg-card rounded-[18px] divide-y divide-line overflow-visible">
              <div className="p-4 relative z-10">
                <label className="block text-sm font-semibold text-slate-900 dark:text-zinc-100 mb-2 text-left">
                  Source Language
                </label>
                <SearchableSelect
                  options={[
                    { value: 'auto', label: '✨ Auto Detect (Recommended)' },
                    { value: 'en', label: 'English' },
                    { value: 'zh', label: 'Chinese (Mandarin)' },
                    { value: 'es', label: 'Spanish' },
                    { value: 'fr', label: 'French' },
                    { value: 'de', label: 'German' },
                    { value: 'ja', label: 'Japanese' },
                  ]}
                  value={options.language}
                  onChange={(val) => setOptions({ ...options, language: val })}
                  placeholder="Select language..."
                  searchPlaceholder="Search languages..."
                />
              </div>

              <div
                className="p-4 flex items-center justify-between hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors cursor-pointer rounded-b-[18px]"
                onClick={() =>
                  setOptions({ ...options, speakerDetection: !options.speakerDetection })
                }
              >
                <div className="flex items-center gap-3 text-left">
                  <div className="bg-emerald-100 dark:bg-emerald-500/10 p-2 rounded-lg shrink-0">
                    <AlertCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-zinc-100">
                      Speaker Identification
                    </p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400">
                      Detect and label different speakers
                    </p>
                  </div>
                </div>
                <div
                  className={`w-11 h-6 rounded-full relative transition-colors duration-200 ${
                    options.speakerDetection ? 'bg-[#34c759]' : 'bg-slate-200 dark:bg-zinc-700'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-200 shadow-sm ${
                      options.speakerDetection ? 'left-6' : 'left-1'
                    }`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        );

      case UploadStep.UPLOADING:
        return (
          <div className="py-2 space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col items-center justify-center">
              <div className="relative h-24 w-24 mb-4">
                <svg className="h-full w-full -rotate-90 transform" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#ededf0"
                    strokeWidth="8"
                    className="dark:stroke-zinc-800"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#007aff"
                    strokeWidth="8"
                    strokeDasharray="283"
                    strokeDashoffset={283 - (283 * progress) / 100}
                    strokeLinecap="round"
                    className="transition-all duration-300 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center flex-col">
                  <span className="text-xl font-bold text-slate-900 dark:text-white">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
              <p className="text-slate-500 dark:text-zinc-400 text-sm font-medium animate-pulse">
                Processing media...
              </p>
            </div>

            <div className="space-y-3 max-w-sm mx-auto">
              {uploadStages.map((stage, idx) => {
                const isComplete = idx < activeStageIndex;
                const isCurrent = idx === activeStageIndex;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 text-sm transition-all duration-300 ${
                      isComplete || isCurrent ? 'opacity-100' : 'opacity-40'
                    }`}
                  >
                    <div
                      className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${
                        isComplete
                          ? 'bg-emerald-500 text-white'
                          : isCurrent
                          ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300'
                          : 'bg-slate-100 dark:bg-zinc-800'
                      }`}
                    >
                      {isComplete ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : isCurrent ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <div className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-zinc-600" />
                      )}
                    </div>
                    <span
                      className={`${
                        isComplete
                          ? 'text-slate-900 dark:text-zinc-200'
                          : isCurrent
                          ? 'text-indigo-600 dark:text-indigo-400 font-medium'
                          : 'text-slate-500 dark:text-zinc-500'
                      }`}
                    >
                      {stage.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case UploadStep.COMPLETE:
        return (
          <div className="py-12 text-center animate-in zoom-in-95 duration-300">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-emerald-100 dark:bg-emerald-500/20 rounded-full animate-ping opacity-20"></div>
              <div className="h-20 w-20 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center relative z-10">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 dark:text-emerald-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              Transcription Started!
            </h3>
            <p className="text-slate-500 dark:text-zinc-400 max-w-xs mx-auto mb-8">
              Your file is being transcribed. Create an account to save your transcription and
              unlock more features.
            </p>
            <div className="flex justify-center items-center gap-2 text-sm text-indigo-600 dark:text-indigo-300 font-medium bg-indigo-50 dark:bg-indigo-500/10 py-2 px-4 rounded-full mx-auto w-fit">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing transcription...
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (step === UploadStep.SELECT_FILE) return null;
    if (step === UploadStep.UPLOADING || step === UploadStep.COMPLETE) return null;

    return (
      <>
        <Button variant="ghost" onClick={() => setStep(UploadStep.SELECT_FILE)}>
          Back
        </Button>
        <Button onClick={startUpload} disabled={!!quota && !quota.can_upload}>
          Start Transcription
        </Button>
      </>
    );
  };

  return (
    <>
      <div className="w-full max-w-2xl mx-auto bg-raised rounded-[24px] shadow-window text-start transition-all">
        {step !== UploadStep.SELECT_FILE && step !== UploadStep.COMPLETE && (
          <div className="px-6 py-4 border-b border-slate-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex justify-between items-center">
            <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
              Free Transcription
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setStep(UploadStep.SELECT_FILE)}>
              Cancel
            </Button>
          </div>
        )}
        <div className="flex flex-col h-full">
          <div className={`p-6 ${step === UploadStep.COMPLETE ? 'pt-10' : ''}`}>
            {renderContent()}
          </div>

          {step === UploadStep.CONFIRM_INFO && (
            <div className="px-6 pb-6 pt-1 flex justify-end gap-2.5 mt-auto">
              {renderFooter()}
            </div>
          )}
        </div>
      </div>

      {/* Registration prompt modal */}
      <RegistrationPromptModal
        isOpen={showPrompt}
        onClose={() => setShowPrompt(false)}
        resetTime={quota?.reset_at ? new Date(quota.reset_at) : undefined}
      />
    </>
  );
};

export default AnonymousUploadFlow;
